import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { Bookkeeping } from "@/models/api/bookkeeping/bookkeeping";
import { FILTER_NAMES } from "@/static/filterNames";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";

//#region Routes
export const URL_GET_BOOKKEEPING_LIST = "/Bookkeeping/BookkeepingList";
export const URL_POST_BOOKKEEPING_LOCK_MONTH = "/Bookkeeping/LockMonth";
export const URL_POST_BOOKKEEPING_UNLOCK_MONTH = "/Bookkeeping/UnlockMonth";
export const URL_POST_BOOKKEEPING_LOCK_ALL_MONTHS = "/Bookkeeping/LockAllMonths";
export const URL_POST_BOOKKEEPING_UNLOCK_ALL_MONTHS = "/Bookkeeping/UnlockAllMonths";
export const URL_POST_BOOKKEEPING_OPTIMA_CASH_REPORT_EXPORT = "/Bookkeeping/OptimaCashReportExport";
export const URL_POST_BOOKKEEPING_OPTIMA_SALE_INVOICE_AND_TRANSFERS =
  "/Bookkeeping/OptimaSaleInvoiceAndTransfersExport";
export const URL_POST_BOOKKEEPING_OPTIMA_VAT_MARGIN_EXPORT = "/Bookkeeping/OptimaVatMarginExport";
export const URL_POST_BOOKKEEPING_OPTIMA_COST_INVOICE_EXPORT = "/Bookkeeping/OptimaCostInvoiceExport";
export const URL_POST_BOOKKEEPING_OPTIMA_SALE_AS_COST_INVOICE_EXPORT = "/Bookkeeping/OptimaSaleAsCostInvoicesExport";
export const URL_POST_BOOKKEEPING_OPTIMA_CREDIT_CARDS_TRANSFERS_EXPORT =
  "/Bookkeeping/OptimaCreditCardsTransfersExport";
export const URL_POST_BOOKKEEPING_OPTIMA_SALES_INVOICE_AS_PURCHASE_INVOICE_EXPORT =
  "/Bookkeeping/OptimaSalesInvoiceAsPurchaseInvoiceExport";
export const URL_POST_BOOKKEEPING_SYMPHONY_COST_INVOICE_EXPORT = "/Bookkeeping/SymfoniaCostInvoiceExport";
export const URL_POST_BOOKKEEPING_SYMPHONY_SALE_INVOICE_EXPORT = "/Bookkeeping/SymfoniaSaleInvoiceExport";
export const URL_POST_BOOKKEEPING_JPK_EXPORT = "/Bookkeeping/JPKExport";
export const URL_POST_BOOKKEEPING_JPKPKP_EXPORT = "/Bookkeeping/JPKPKPExport";
export const URL_POST_BOOKKEEPING_JPKFA_EXPORT = "/Bookkeeping/JPKFAExport";

//#endregion

const _getBookkeepingList = new CancellableHelper<Bookkeeping>(URL_GET_BOOKKEEPING_LIST);
_getBookkeepingList.jsonSerializer = true;
export const getBookkeepingList = async (genericTableRequest: GenericTableRequest): Promise<Bookkeeping> => {
  return await _getBookkeepingList.get(genericTableRequest);
};

export const lockMonth = async (month: number, year: number, officeProfileId: number | null): Promise<null> => {
  const _lockMonth = new CancellableHelper<null>(URL_POST_BOOKKEEPING_LOCK_MONTH, null, true);
  return await _lockMonth.post({ month: month, year: year, officeProfileId: officeProfileId }, null);
};

export const unlockMonth = async (month: number, year: number, officeProfileId: number | null): Promise<null> => {
  const _unlockMonth = new CancellableHelper<null>(URL_POST_BOOKKEEPING_UNLOCK_MONTH, null, true);
  return await _unlockMonth.post({ month: month, year: year, officeProfileId: officeProfileId }, null);
};

export const lockAllMonths = async (month: number, year: number): Promise<null> => {
  const _lockAllMonths = new CancellableHelper<null>(URL_POST_BOOKKEEPING_LOCK_ALL_MONTHS, null, true);
  return await _lockAllMonths.post({ month: month, year: year }, null);
};

export const unlockAllMonths = async (month: number, year: number): Promise<null> => {
  const _unlockAllMonths = new CancellableHelper<null>(URL_POST_BOOKKEEPING_UNLOCK_ALL_MONTHS, null, true);
  return await _unlockAllMonths.post({ month: month, year: year }, null);
};

const _optimaCashReportExport = new CancellableHelper<File>(URL_POST_BOOKKEEPING_OPTIMA_CASH_REPORT_EXPORT);
_optimaCashReportExport.jsonSerializer = true;
_optimaCashReportExport.isFile = true;
export const optimaCashReportExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaCashReportExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _optimaSaleInvoiceAndTransfersExport = new CancellableHelper<File>(
  URL_POST_BOOKKEEPING_OPTIMA_SALE_INVOICE_AND_TRANSFERS
);
_optimaSaleInvoiceAndTransfersExport.jsonSerializer = true;
_optimaSaleInvoiceAndTransfersExport.isFile = true;
export const optimaSaleInvoiceAndTransfersExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaSaleInvoiceAndTransfersExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _optimaVatMarginExport = new CancellableHelper<File>(URL_POST_BOOKKEEPING_OPTIMA_VAT_MARGIN_EXPORT);
_optimaVatMarginExport.jsonSerializer = true;
_optimaVatMarginExport.isFile = true;
export const optimaVatMarginExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaVatMarginExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _optimaCostInvoiceExport = new CancellableHelper<File>(URL_POST_BOOKKEEPING_OPTIMA_COST_INVOICE_EXPORT);
_optimaCostInvoiceExport.jsonSerializer = true;
_optimaCostInvoiceExport.isFile = true;
export const optimaCostInvoiceExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaCostInvoiceExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _optimaSaleAsCostInvoicesExport = new CancellableHelper<File>(
  URL_POST_BOOKKEEPING_OPTIMA_SALE_AS_COST_INVOICE_EXPORT
);
_optimaSaleAsCostInvoicesExport.jsonSerializer = true;
_optimaSaleAsCostInvoicesExport.isFile = true;
export const optimaSaleAsCostInvoicesExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaSaleAsCostInvoicesExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _optimaCreditCardsTransfersExport = new CancellableHelper<File>(
  URL_POST_BOOKKEEPING_OPTIMA_CREDIT_CARDS_TRANSFERS_EXPORT
);
_optimaCreditCardsTransfersExport.jsonSerializer = true;
_optimaCreditCardsTransfersExport.isFile = true;
export const optimaCreditCardsTransfersExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaCreditCardsTransfersExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _optimaSalesInvoiceAsPurchaseInvoiceExport = new CancellableHelper<File>(
  URL_POST_BOOKKEEPING_OPTIMA_SALES_INVOICE_AS_PURCHASE_INVOICE_EXPORT
);
_optimaSalesInvoiceAsPurchaseInvoiceExport.jsonSerializer = true;
_optimaSalesInvoiceAsPurchaseInvoiceExport.isFile = true;
export const optimaSalesInvoiceAsPurchaseInvoiceExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _optimaSalesInvoiceAsPurchaseInvoiceExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _symphonyCostInvoiceExport = new CancellableHelper<File>(URL_POST_BOOKKEEPING_SYMPHONY_COST_INVOICE_EXPORT);
_symphonyCostInvoiceExport.jsonSerializer = true;
_symphonyCostInvoiceExport.isFile = true;
export const symphonyCostInvoiceExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _symphonyCostInvoiceExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _symphonySaleInvoiceExport = new CancellableHelper<File>(URL_POST_BOOKKEEPING_SYMPHONY_SALE_INVOICE_EXPORT);
_symphonySaleInvoiceExport.jsonSerializer = true;
_symphonySaleInvoiceExport.isFile = true;
export const symphonySaleInvoiceExport = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _symphonySaleInvoiceExport.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _exportJPK = new CancellableHelper<File>(URL_POST_BOOKKEEPING_JPK_EXPORT);
_exportJPK.jsonSerializer = true;
_exportJPK.isFile = true;
export const exportJPK = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _exportJPK.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _exportJPKPKP = new CancellableHelper<File>(URL_POST_BOOKKEEPING_JPKPKP_EXPORT);
_exportJPKPKP.jsonSerializer = true;
_exportJPKPKP.isFile = true;
export const exportJPKPKP = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _exportJPKPKP.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};

const _exportJPKFA = new CancellableHelper<File>(URL_POST_BOOKKEEPING_JPKFA_EXPORT);
_exportJPKFA.jsonSerializer = true;
_exportJPKFA.isFile = true;
export const exportJPKFA = async (
  criterions: Array<CriterionItemRequest> | null,
  filterName: FILTER_NAMES
): Promise<void> => {
  const response = await _exportJPKFA.post(
    {
      criterions: criterions,
      filterName: filterName,
    },
    {}
  );
  const blob = new Blob([response], { type: response.type });
  CancellableHelper.downloadFile(blob, response.name);
};
